import { Container, Graphics } from 'pixi.js-legacy';
import type { Bullet } from '../../entities/Bullet';
import type { OneWayBullet } from '../../entities/OneWayBullet';

/**
 * Bullet Graphics 오브젝트 풀
 * PixiJS Graphics 객체를 재사용하여 메모리 할당/해제 비용 절감
 */
export class BulletPool {
    private pool: Graphics[] = [];
    private active: Map<Bullet | OneWayBullet, Graphics> = new Map();
    private container: Container;
    // iOS: 매우 공격적인 풀 크기 제한 (50개)
    private maxPoolSize: number = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 50 : 150;

    constructor(container: Container) {
        this.container = container;
    }

    /**
     * 풀에서 Graphics 가져오기 (없으면 생성)
     */
    get(bullet: Bullet | OneWayBullet): Graphics {
        let g = this.pool.pop();

        if (!g) {
            g = new Graphics();
            // 기본 원 (반지름 1, scale로 크기 조절)
            g.beginFill(0xFFFFFF);
            g.drawCircle(0, 0, 1);
            g.endFill();
        }

        // 위치 및 크기 설정
        g.position.set(bullet.x, bullet.y);
        g.scale.set(bullet.radius);
        g.visible = true;
        g.alpha = 1;

        // 색상 설정 (분열 총알은 다른 색)
        if ('canSplit' in bullet && bullet.isSplitBullet()) {
            g.tint = 0xFFAAAA; // 연한 빨강
        } else {
            g.tint = 0xFFFFFF;
        }

        this.active.set(bullet, g);
        this.container.addChild(g);

        return g;
    }

    /**
     * Graphics를 풀로 반환
     */
    release(bullet: Bullet | OneWayBullet): void {
        const g = this.active.get(bullet);
        if (g) {
            g.visible = false;
            this.container.removeChild(g);
            this.active.delete(bullet);

            // iOS 메모리 관리: 풀 크기 제한 초과 시 destroy
            if (this.pool.length < this.maxPoolSize) {
                this.pool.push(g);
            } else {
                g.destroy();
            }
        }
    }

    /**
     * 특정 총알의 Graphics 업데이트
     */
    update(bullet: Bullet | OneWayBullet): void {
        const g = this.active.get(bullet);
        if (g) {
            g.position.set(bullet.x, bullet.y);
            g.scale.set(bullet.radius);
        }
    }

    /**
     * 총알이 등록되어 있는지 확인
     */
    has(bullet: Bullet | OneWayBullet): boolean {
        return this.active.has(bullet);
    }

    /**
     * 활성 총알 반복자
     */
    getActiveBullets(): IterableIterator<Bullet | OneWayBullet> {
        return this.active.keys();
    }

    /**
     * 모든 Graphics 정리
     */
    clear(): void {
        for (const [bullet, g] of this.active) {
            g.visible = false;
            this.container.removeChild(g);
            // 풀 크기 제한 초과 시 destroy
            if (this.pool.length < this.maxPoolSize) {
                this.pool.push(g);
            } else {
                g.destroy();
            }
        }
        this.active.clear();
    }

    /**
     * 참조 초기화 (clearAll 후 다시 생성할 수 있도록)
     */
    reset(): void {
        // clearAll()로 이미 컨테이너에서 제거되었으므로 참조만 정리
        this.active.clear();
        // 풀의 모든 Graphics destroy
        for (const g of this.pool) {
            g.destroy();
        }
        this.pool = [];
    }

    /**
     * 완전히 파괴
     */
    destroy(): void {
        this.clear();
        for (const g of this.pool) {
            g.destroy();
        }
        this.pool = [];
    }

    /**
     * 활성 총알 수
     */
    get activeCount(): number {
        return this.active.size;
    }

    /**
     * 풀 크기
     */
    get poolSize(): number {
        return this.pool.length;
    }
}

/**
 * 총알 어댑터 - BulletSpawner와 PixiJS 사이의 브릿지
 */
export class BulletAdapter {
    private bulletPool: BulletPool;
    private oneWayBulletPool: BulletPool;

    constructor(bulletContainer: Container, oneWayBulletContainer: Container) {
        this.bulletPool = new BulletPool(bulletContainer);
        this.oneWayBulletPool = new BulletPool(oneWayBulletContainer);
    }

    /**
     * 총알 목록 동기화 (추가/제거/업데이트)
     * Set 생성 없이 직접 동기화하여 메모리 할당 최소화
     */
    syncBullets(bullets: Bullet[]): void {
        // 빈 배열이면 모든 Graphics 정리 (폭탄 효과 등)
        if (bullets.length === 0) {
            this.bulletPool.clear();
            return;
        }

        // 1. 죽은 총알 또는 배열에서 제거된 총알 release
        // BulletSpawner에서 clearAllBullets 후 배열이 비어있으면 모든 Graphics 정리
        const bulletSet = new Set(bullets);
        for (const bullet of this.bulletPool.getActiveBullets()) {
            if (!bullet.isAlive || !bulletSet.has(bullet as Bullet)) {
                this.bulletPool.release(bullet);
            }
        }

        // 2. 살아있는 총알 업데이트/추가
        for (const bullet of bullets) {
            if (this.bulletPool.has(bullet)) {
                this.bulletPool.update(bullet);
            } else {
                this.bulletPool.get(bullet);
            }
        }
    }

    /**
     * 단방향 총알 목록 동기화
     */
    syncOneWayBullets(bullets: OneWayBullet[]): void {
        // 빈 배열이면 모든 Graphics 정리 (폭탄 효과 등)
        if (bullets.length === 0) {
            this.oneWayBulletPool.clear();
            return;
        }

        // 1. 죽은 총알 또는 배열에서 제거된 총알 release
        const bulletSet = new Set(bullets);
        for (const bullet of this.oneWayBulletPool.getActiveBullets()) {
            if (!bullet.isAlive || !bulletSet.has(bullet as OneWayBullet)) {
                this.oneWayBulletPool.release(bullet);
            }
        }

        // 2. 살아있는 총알 업데이트/추가
        for (const bullet of bullets) {
            if (this.oneWayBulletPool.has(bullet)) {
                this.oneWayBulletPool.update(bullet);
            } else {
                this.oneWayBulletPool.get(bullet);
            }
        }
    }

    /**
     * 모든 풀 정리
     */
    clear(): void {
        this.bulletPool.clear();
        this.oneWayBulletPool.clear();
    }

    /**
     * 참조 초기화 (clearAll 후 다시 생성할 수 있도록)
     */
    reset(): void {
        this.bulletPool.reset();
        this.oneWayBulletPool.reset();
    }

    /**
     * 완전히 파괴
     */
    destroy(): void {
        this.bulletPool.destroy();
        this.oneWayBulletPool.destroy();
    }

    /**
     * 디버그 정보
     */
    getDebugInfo(): { bullets: { active: number; pool: number }; oneWay: { active: number; pool: number } } {
        return {
            bullets: {
                active: this.bulletPool.activeCount,
                pool: this.bulletPool.poolSize
            },
            oneWay: {
                active: this.oneWayBulletPool.activeCount,
                pool: this.oneWayBulletPool.poolSize
            }
        };
    }
}
